import { getJson, send } from './client';
import type { DispatchFeedAnswer, DispatchMetricData, DispatchMetricList } from './types';

/**
 * The operations dashboard's API.
 *
 * Everything here proxies dispatch — the metrics are its rows, not the CRM's —
 * so the service is a forwarder and these calls carry no CRM ids at all. See
 * services/dispatchMetrics.js for why nothing is stored on our side.
 *
 * `from`/`to` are UTC INSTANTS, not civil dates, and that is the opposite of
 * the analytics API on purpose: dispatch buckets on real timestamps, so the
 * tenant's day boundaries have to be resolved into instants before the call
 * rather than after it. `frames.ts` is the one place that does that conversion.
 */

export interface MetricDataRequest {
  category: string;
  subcategories: string[];
}

export interface MetricDataQuery {
  from: string | null;
  to: string | null;
  groupBy: string;
  requests: MetricDataRequest[];
}

/** One dispatch card: its id and the buckets it draws. */
export interface FeedMetricRequest {
  id: number;
  subcategories: string[];
}

export interface FeedQuery {
  from: string;
  to: string;
  metrics: FeedMetricRequest[];
}

export const listMetrics = () => getJson<DispatchMetricList>('/dispatch_metrics');

/**
 * Every card's data in one round trip.
 *
 * One request per CATEGORY, not per card: with the window chosen once for the
 * page, two cards on the same category differ only in which subcategories they
 * draw, so the union of their subcategories answers both.
 */
export const fetchMetricData = (query: MetricDataQuery) =>
  send<{ payload: DispatchMetricData[] }>('POST', '/dispatch_metrics/data', query).then(
    (body) => body.payload,
  );

/**
 * The dispatch row, counted from the job board rather than read from the
 * metrics API.
 *
 * Per METRIC, not per category, and that is the whole reason it is a second
 * call. A dispatch card's figure is a union over its own buckets — a job in two
 * of them counts once — so the per-category union the other batch relies on
 * cannot be re-split back into cards. The upstream cost is unchanged: the board
 * is read once for the page however many dispatch cards are on it.
 *
 * There is no `groupBy`: the answer is one point per pickup day and `series.ts`
 * buckets it into whatever the page is showing, the same as any other card.
 */
export const fetchFeedCounts = (query: FeedQuery) =>
  send<DispatchFeedAnswer>('POST', '/dispatch_metrics/feed', query);

/**
 * The order the cards were dragged into, highest priority first.
 *
 * The whole list goes every time rather than the one card that moved: dispatch
 * orders on a `priority` integer that carries ties from years of hand-set
 * numbers, and an order written on top of ties reshuffles itself on the next
 * read. The server re-strikes them all from the top.
 */
export const reorderMetrics = (order: number[]) =>
  send<{ order: number[]; written: number }>('PATCH', '/dispatch_metrics/reorder', { order });

/**
 * Create and edit both send the WHOLE metric form, because dispatch's update
 * takes the same body its create does — a PATCH carrying only the field that
 * moved blanks every field it left out. `writePayload` in metricForm.ts is the
 * one place that builds it, and it is also what keeps a metric's stored time
 * shape intact through an edit made from a screen that has no control for it.
 */
export interface MetricWrite {
  category: string;
  subCategory: string[];
  metricsName: string;
  groupBy: string;
  arrangeBy: string;
  type: string;
  priority: number;
}

export const createMetric = (form: MetricWrite) =>
  send<{ created: boolean }>('POST', '/dispatch_metrics', form);

export const updateMetric = (id: number, form: MetricWrite) =>
  send<{ updated: boolean }>('PATCH', `/dispatch_metrics/${id}`, form);

export const deleteMetric = (id: number) => send<{ deleted: boolean }>('DELETE', `/dispatch_metrics/${id}`);
